import {
  MENTION_EMAIL_REGEX,
  normalizeCommentTextForDisplay,
  parseLinksInPlainText,
  SHARED_LINK_LABEL,
} from 'src/utils/commentMentions';
import 'src/components/general/commentLinks.scss';

/**
 * Convierte markdown básico a HTML de forma segura para mostrar en la UI.
 * Soporta: **negrita**, *cursiva*, _cursiva_, __subrayado__, listas (- item), saltos de línea.
 * Escapa HTML para evitar XSS.
 */
function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/** Filas tipo | a | b | (GFM). */
function splitMarkdownTableCells(line) {
  const raw = String(line || '').replace(/\r$/, '').trim();
  if (!raw.includes('|')) return null;
  const parts = raw.split('|').map((c) => c.trim());
  while (parts.length && parts[0] === '') parts.shift();
  while (parts.length && parts[parts.length - 1] === '') parts.pop();
  return parts.length ? parts : null;
}

function isMarkdownTableSeparatorRow(cells) {
  return (
    Array.isArray(cells) &&
    cells.length >= 2 &&
    cells.every((c) => /^:?\s*-{2,}\s*:?$/.test(String(c).trim()))
  );
}

/**
 * Tabla GFM: cabecera, fila |---|---|, cuerpo hasta línea en blanco.
 * @returns {{ html: string, nextIdx: number } | null}
 */
function tryParseMarkdownTable(lines, startIdx) {
  if (startIdx >= lines.length - 1) return null;
  const header = splitMarkdownTableCells(lines[startIdx]);
  if (!header || header.length < 2) return null;
  const sep = splitMarkdownTableCells(lines[startIdx + 1]);
  if (!sep || sep.length < header.length || !isMarkdownTableSeparatorRow(sep.slice(0, header.length))) return null;
  let j = startIdx + 2;
  const body = [];
  while (j < lines.length) {
    const t = String(lines[j] || '').trim();
    if (t === '') break;
    if (!t.includes('|')) break;
    const row = splitMarkdownTableCells(lines[j]);
    if (!row || row.length < 2) break;
    body.push(row);
    j += 1;
  }
  if (body.length === 0) return null;
  const ncol = header.length;
  const esc = escapeHtml;
  const headHtml = `<tr>${header.map((c) => `<th>${esc(c)}</th>`).join('')}</tr>`;
  const bodyHtml = body
    .map((row) => {
      const cells = [...row];
      while (cells.length < ncol) cells.push('');
      return `<tr>${cells.slice(0, ncol).map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`;
    })
    .join('');
  const html =
    '<div class="bitacora-md-table-wrap"><table class="bitacora-md-table"><thead>' +
    headHtml +
    '</thead><tbody>' +
    bodyHtml +
    '</tbody></table></div>';
  return { html, nextIdx: j };
}

export function renderMarkdownToHtml(text) {
  if (!text || typeof text !== 'string') return '';
  let html = escapeHtml(text);

  // Orden: subrayado (__), negrita (**), cursiva (*), cursiva (_)
  html = html.replace(/__(.+?)__/g, '<u>$1</u>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  // _cursiva_ (Markdown estándar); después de __subrayado__. Cierre con lookahead para no romper _text_**negrita**
  html = html.replace(/(^|[\s(>])_([^_\n]+?)_(?=[\s).,;:!?*<]|$)/g, '$1<em>$2</em>');

  // Separador horizontal: ---, ***, ___
  const isHrLine = (s) => /^[-*_]{2,}\s*$/.test(String(s).trim());

  // Listas: líneas que empiezan con "- " se convierten en <ul><li>...</li></ul>
  const lines = html.split('\n');
  const result = [];
  let i = 0;
  while (i < lines.length) {
    if (isHrLine(lines[i])) {
      result.push('<hr class="bitacora-editor-hr" />');
      i += 1;
      continue;
    }
    if (lines[i].startsWith('- ')) {
      const listItems = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        listItems.push('<li>' + lines[i].slice(2) + '</li>');
        i += 1;
      }
      result.push('<ul>' + listItems.join('') + '</ul>');
    } else {
      result.push(lines[i]);
      i += 1;
    }
  }
  html = result.join('\n');
  html = html.replace(/\n/g, '<br />');

  return html;
}

const looksLikeHtml = (text = '') => /<\/?[a-z][\s\S]*>/i.test(text);
const mentionRegex = MENTION_EMAIL_REGEX;

/**
 * Mismo criterio que en la creación de bitácora: si el texto es HTML se usa tal cual;
 * si es markdown (o plano) se pasa por renderMarkdownToHtml.
 */
export function renderBitacoraComment(text = '') {
  if (!text) return '';
  // Para mantener consistencia visual con el editor de bitácora,
  // el markdown/plain se convierte con el parser rico del editor.
  return looksLikeHtml(text) ? text : markdownToEditorHtml(text);
}

/** Resalta @mentions (emails) en comentarios, compatible con texto plano y markdown. */
export function renderBitacoraCommentWithMentions(text = '', mentionStyle = {}) {
  if (!text) return '';
  const normalizedText = normalizeCommentTextForDisplay(text);
  const defaultStyle = {
    backgroundColor: '#e6f7f6',
    color: '#0f766e',
    border: '1px solid #bfe9e5',
    padding: '1px 7px',
    borderRadius: '999px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '11px',
    lineHeight: '1.4',
    whiteSpace: 'nowrap',
    verticalAlign: 'baseline',
    margin: '0 4px 0 0',
    ...mentionStyle,
  };
  const styleStr = Object.entries(defaultStyle).map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}:${v}`).join(';');

  const highlightMentionsInHtml = (html) => {
    if (!html || typeof document === 'undefined') return html || '';
    const container = document.createElement('div');
    container.innerHTML = html;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let node = walker.nextNode();
    while (node) {
      nodes.push(node);
      node = walker.nextNode();
    }
    nodes.forEach((textNode) => {
      const parentElement = textNode.parentElement;
      if (parentElement?.closest('.bitacora-editor-mention, .bitacora-comment-mention')) return;
      const content = textNode.nodeValue || '';
      if (!mentionRegex.test(content)) return;
      mentionRegex.lastIndex = 0;
      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      content.replace(mentionRegex, (match, _g1, offset) => {
        if (offset > lastIndex) {
          fragment.appendChild(document.createTextNode(content.slice(lastIndex, offset)));
        }
        const span = document.createElement('span');
        span.className = 'bitacora-comment-mention';
        span.setAttribute('style', styleStr);
        span.textContent = match;
        fragment.appendChild(span);
        lastIndex = offset + match.length;
        return match;
      });
      if (lastIndex < content.length) {
        fragment.appendChild(document.createTextNode(content.slice(lastIndex)));
      }
      textNode.parentNode?.replaceChild(fragment, textNode);
    });
    return container.innerHTML;
  };

  const linkifyUrlsInHtml = (html) => {
    if (!html || typeof document === 'undefined') return html || '';
    const container = document.createElement('div');
    container.innerHTML = html;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let node = walker.nextNode();
    while (node) {
      nodes.push(node);
      node = walker.nextNode();
    }
    nodes.forEach((textNode) => {
      const parentElement = textNode.parentElement;
      if (
        parentElement?.closest('.comment-link, .bitacora-comment-mention, .bitacora-editor-mention') ||
        parentElement?.closest('a')
      ) {
        return;
      }
      const content = textNode.nodeValue || '';
      const parts = parseLinksInPlainText(content);
      if (!parts.some((part) => part.type === 'link')) return;

      const fragment = document.createDocumentFragment();
      parts.forEach((part) => {
        if (part.type === 'link') {
          const wrap = document.createElement('span');
          wrap.className = 'comment-link-cell';
          const anchor = document.createElement('a');
          anchor.className = 'comment-link';
          anchor.href = part.href;
          anchor.target = '_blank';
          anchor.rel = 'noopener noreferrer';
          const textSpan = document.createElement('span');
          textSpan.className = 'comment-link__text';
          textSpan.textContent = part.label || SHARED_LINK_LABEL;
          anchor.appendChild(textSpan);
          const icon = document.createElement('i');
          icon.className = 'arrow up right icon comment-link-hover-icon';
          wrap.appendChild(anchor);
          wrap.appendChild(icon);
          fragment.appendChild(wrap);
          return;
        }
        fragment.appendChild(document.createTextNode(part.value));
      });
      textNode.parentNode?.replaceChild(fragment, textNode);
    });
    return container.innerHTML;
  };

  return linkifyUrlsInHtml(highlightMentionsInHtml(renderBitacoraComment(normalizedText)));
}

/**
 * Convierte texto/markdown de la IA a HTML listo para el editor (Tiptap):
 * párrafos <p>, títulos <h1>/<h2>/<h3>, listas <ul>/<ol><li>, separador <hr>, negrita/cursiva/subrayado.
 * Soporta formato del análisis: ## Sección, **n. Título** (preguntas) y - viñetas.
 */
export function markdownToEditorHtml(text) {
  if (!text || typeof text !== 'string') return '<p></p>';
  const raw = text.trim();
  if (!raw) return '<p></p>';

  if (looksLikeHtml(raw)) return raw;

  const isHorizontalRule = (s) => /^[-*_]{2,}\s*$/.test((s || '').trim());
  const isNumberedLine = (s) => /^\d+\.\s/.test(s);
  const isBulletLine = (s) => /^[-*]\s/.test(s) || /^[•–—]\s/.test(s);
  // Formato IA normalizado: **n. Título** (una línea completa en negrita)
  const isBoldNumberedTitle = (s) => /^\*\*\d+\.\s+.+\*\*$/.test(s);

  const lines = raw.split(/\n/);
  const out = [];
  let listTag = null; // 'ol' | 'ul' | null
  let listItems = [];
  let paragraphLines = [];

  function flushList() {
    if (listTag && listItems.length > 0) {
      out.push('<' + listTag + '>' + listItems.join('') + '</' + listTag + '>');
      listItems = [];
      listTag = null;
    }
  }
  function flushParagraph() {
    if (paragraphLines.length > 0) {
      const inline = paragraphLines.map((l) => renderMarkdownToHtml(l)).join('<br />');
      out.push('<p>' + inline + '</p>');
      paragraphLines = [];
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (trimmed === '') {
      flushList();
      flushParagraph();
      continue;
    }

    if (trimmed.includes('|')) {
      const tbl = tryParseMarkdownTable(lines, i);
      if (tbl) {
        flushList();
        flushParagraph();
        out.push(tbl.html);
        i = tbl.nextIdx - 1;
        continue;
      }
    }

    if (trimmed.startsWith('#### ')) {
      flushList();
      flushParagraph();
      out.push('<h4>' + escapeHtml(trimmed.slice(5).trim()) + '</h4>');
      continue;
    }
    if (trimmed.startsWith('### ')) {
      flushList();
      flushParagraph();
      out.push('<h3>' + escapeHtml(trimmed.slice(4).trim()) + '</h3>');
      continue;
    }
    if (trimmed.startsWith('## ')) {
      flushList();
      flushParagraph();
      out.push('<h2>' + escapeHtml(trimmed.slice(3).trim()) + '</h2>');
      continue;
    }
    if (trimmed.startsWith('# ')) {
      flushList();
      flushParagraph();
      out.push('<h1>' + escapeHtml(trimmed.slice(2).trim()) + '</h1>');
      continue;
    }
    if (isHorizontalRule(trimmed)) {
      flushList();
      flushParagraph();
      out.push('<hr class="bitacora-editor-hr" />');
      continue;
    }
    if (isBoldNumberedTitle(trimmed)) {
      flushList();
      flushParagraph();
      out.push('<p>' + renderMarkdownToHtml(trimmed) + '</p>');
      continue;
    }
    if (isNumberedLine(line)) {
      flushParagraph();
      const content = line.replace(/^\d+\.\s+/, '');
      if (listTag !== 'ol') {
        flushList();
        listTag = 'ol';
      }
      listItems.push('<li>' + renderMarkdownToHtml(content) + '</li>');
      continue;
    }
    if (isBulletLine(line)) {
      flushParagraph();
      const content = line.replace(/^[-*]\s+/, '').replace(/^[•–—]\s+/, '').trim() || line;
      if (listTag !== 'ul') {
        flushList();
        listTag = 'ul';
      }
      listItems.push('<li>' + renderMarkdownToHtml(content) + '</li>');
      continue;
    }

    flushList();
    paragraphLines.push(line);
  }

  flushList();
  flushParagraph();

  return out.length ? out.join('') : '<p></p>';
}

/**
 * Convierte HTML del editor a formato texto/markdown compatible con backend
 * para almacenar en emailDraft.
 */
export function htmlToEmailDraftFormat(text = '') {
  if (!text || typeof text !== 'string') return '';
  const trimmed = text.trim();
  if (!trimmed) return '';
  // Si no parece HTML, devolver tal cual (ya es markdown/plain)
  if (!/<\/?[a-z][^>]*>/i.test(trimmed)) return trimmed;

  const div = document.createElement('div');
  div.innerHTML = trimmed;

  const walk = (node, parts = []) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent || '');
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = (node.tagName || '').toLowerCase();
      if (tag === 'br') {
        parts.push('\n');
        return;
      }
      if (tag === 'hr') {
        parts.push('\n---\n');
        return;
      }
      if (tag === 'p' || tag === 'div') {
        if (parts.length) parts.push('\n');
      }
      if (tag === 'h1') parts.push('\n# ');
      if (tag === 'h2') parts.push('\n## ');
      if (tag === 'h3') parts.push('\n### ');
      if (tag === 'h4') parts.push('\n#### ');
      if (tag === 'li') parts.push('\n- ');
      if (tag === 'strong' || tag === 'b') parts.push('**');
      for (let i = 0; i < node.childNodes.length; i++) walk(node.childNodes[i], parts);
      if (tag === 'strong' || tag === 'b') parts.push('**');
      if (tag === 'p' || tag === 'div' || tag === 'li') parts.push('\n');
      if (tag === 'ul' || tag === 'ol') parts.push('\n');
      if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4') parts.push('\n');
    }
  };

  const parts = [];
  for (let i = 0; i < div.childNodes.length; i++) walk(div.childNodes[i], parts);
  return parts.join('').replace(/\n{3,}/g, '\n\n').trim();
}
